import React, { useState, useEffect, useCallback } from 'react';
import './Walkthrough.css';

const DEFAULT_STEPS = [
  {
    selector: null,
    title: 'Welcome to Easy Uploads 👋',
    description: "Let's take a quick tour so you can hit the ground running. Use the arrows or press Next to continue.",
    placement: 'center',
  },
  {
    selector: '.el-create-event-btn',
    title: 'Create an Event',
    description: 'Start here — create a new event for a wedding, birthday, or any shoot. Each event gets its own folder workspace.',
    placement: 'bottom',
  },
  {
    selector: '.el-search-wrap',
    title: 'Search Events',
    description: 'Find any event instantly by name. Type at least 3 characters to trigger the search.',
    placement: 'bottom',
  },
  {
    selector: '.el-credits-badge',
    title: 'Your Credits',
    description: 'Each photo you upload uses 1 credit. Your current balance is always visible here.',
    placement: 'bottom',
  },
  {
    selector: '.el-buy-credits-btn',
    title: 'Buy Credits',
    description: 'Running low? Top up your credits anytime to keep your uploads going.',
    placement: 'bottom',
  },
  {
    selector: 'button[title="Settings"]',
    title: 'Settings',
    description: 'Jump straight to your profile settings to update your details, branding, and preferences.',
    placement: 'bottom',
  },
  {
    selector: '.profile-photo-event',
    title: 'Your Profile',
    description: 'Click here to edit your profile, view transactions, manage your portfolio, or log out.',
    placement: 'left',
  },
  {
    selector: '.event-cards-event .event-card',
    title: 'Event Cards',
    description: 'Click any event card to open its folders. You can organise photos by scene, location, or moment.',
    placement: 'top',
  },
];

const PADDING = 8;
const TOOLTIP_WIDTH = 320;

export default function Walkthrough({ onDone, steps }) {
  const STEPS = steps || DEFAULT_STEPS;
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const current = STEPS[step];

  const updateRect = useCallback(() => {
    if (!current.selector) { setRect(null); return; }
    const el = document.querySelector(current.selector);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      setTimeout(() => setRect(el.getBoundingClientRect()), 80);
    } else {
      setRect(null);
    }
  }, [current.selector]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [updateRect]);

  const goNext = () => step < STEPS.length - 1 ? setStep(s => s + 1) : onDone();
  const goPrev = () => step > 0 && setStep(s => s - 1);

  const tooltipStyle = () => {
    if (!rect || current.placement === 'center') {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: TOOLTIP_WIDTH };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 14;
    const clampX = (x) => Math.min(Math.max(x, 12), vw - TOOLTIP_WIDTH - 12);

    switch (current.placement) {
      case 'bottom':
        return { position: 'fixed', top: rect.bottom + GAP, left: clampX(rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2), width: TOOLTIP_WIDTH };
      case 'top':
        return { position: 'fixed', bottom: vh - rect.top + GAP, left: clampX(rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2), width: TOOLTIP_WIDTH };
      case 'left':
        return { position: 'fixed', top: Math.min(rect.top, vh - 200), right: vw - rect.left + GAP, width: TOOLTIP_WIDTH };
      case 'right':
        return { position: 'fixed', top: Math.min(rect.top, vh - 200), left: rect.right + GAP, width: TOOLTIP_WIDTH };
      default:
        return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: TOOLTIP_WIDTH };
    }
  };

  const arrowClass = () => {
    if (!rect || current.placement === 'center') return '';
    return `wt-tooltip--arrow-${current.placement}`;
  };

  return (
    <div className="wt-root">
      {/* Dark overlay blocked to clicks */}
      <div className="wt-backdrop" />

      {/* Spotlight cutout */}
      {rect && (
        <div
          className="wt-spotlight"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          }}
        />
      )}

      {/* Tooltip */}
      <div className={`wt-tooltip ${arrowClass()}`} style={tooltipStyle()} onClick={e => e.stopPropagation()}>
        <div className="wt-header">
          <span className="wt-counter">{step + 1} / {STEPS.length}</span>
          <button className="wt-skip" onClick={onDone}>Skip tour</button>
        </div>
        <h3 className="wt-title">{current.title}</h3>
        <p className="wt-desc">{current.description}</p>
        <div className="wt-footer">
          <div className="wt-dots">
            {STEPS.map((_, i) => (
              <span key={i} className={`wt-dot${i === step ? ' wt-dot--active' : ''}`} />
            ))}
          </div>
          <div className="wt-btns">
            {step > 0 && (
              <button className="wt-btn wt-btn--back" onClick={goPrev}>Back</button>
            )}
            <button className="wt-btn wt-btn--next" onClick={goNext}>
              {step === STEPS.length - 1 ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
